// src/pages/Onboarding/OnboardingPage.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HelmetTemplate from '../../utils/HelmetTemplate';
import OptionSelectSlide from './slides/OptionSelectSlide';
import SelectDropdownSlide from './slides/SelectDropdownSlide';
import DisplaySlide from './slides/DisplaySlide';
import NameInputSlide from './slides/NameInputSlide';
import BirthdateSlide from './slides/BirthdateSlide';
import UploadSlide from './slides/UploadSlide';
import ImageAnalysisSlide from './slides/ImageAnalysisSlide';
import BeforeAfterSlide from './slides/BeforeAfterSlide';
import TikTokEmbedSlide from './slides/TikTokEmbedSlide';
import StressDisplaySlide from './slides/DisplayChartSlides/StressDisplaySlide';
import BudgetDisplaySlide from './slides/DisplayChartSlides/BudgetDisplaySlide';
import StrongCandidateDisplaySlide from './slides/DisplayChartSlides/StrongCandidateDisplaySlide';
import RedDisplaySlide from './slides/RedDisplaySlide';
import GoodTasteDisplaySlide from './slides/DisplayChartSlides/GoodTasteDisplaySlide'; 
import TeamDisplaySlide from './slides/DisplayChartSlides/TeamDisplaySlide';
import AestheticGoalsSlide from './slides/DisplayChartSlides/AestheticGoalsSlide';
import MedicalBackgroundSlide from './slides/DisplayChartSlides/MedicalBackgroundSlide';
import ReviewsIntroSlide from './slides/DisplayChartSlides/ReviewsIntroSlide';
import ContactInfoSlide from './slides/ContactInfoSlide';
import EmailSlide from './slides/EmailSlide';
import CoverSlide from './slides/CoverSlide';
import ConversionSlide from './slides/ConversionSlide';
import FinalConversionSlide from './slides/FinalConversionSlide';
import { createInitialOnboardingState, applyFieldUpdates, markFieldsAsSynced, recordSyncError } from '../../utils/Onboarding/onboardingState';
import { computeLocalFingerprint, buildSyncPayload } from '../../utils/Onboarding/sync';
import { useOnboardingAssetPreloader } from '../../utils/Onboarding/assetPreloader';
import { registerTintableSvg } from '../../utils/Onboarding/tintableSvg';
import { getImageUploadArtifact } from '../../utils/Onboarding/artifacts';
import { db } from '../../firebase';
import { arrayUnion, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { initializeSession, trackEvent } from '../../utils/Tracking';
import { linkOnboardingToSession } from '../../utils/Tracking';
import { updateOnboardingMaxStep } from '../../utils/Tracking';
import { mapToPhoneCountryCode } from '../../utils/countryCodeMapper';

import countries from '../../data/countries.json'; // assumes this path/filename

// Images

import reclaimConfidenceImage from '../../images/reclaim-your-confidence.svg';
import avoidHorrorStoriesImage from '../../images/avoid-horror-stories.svg';
import dontOverpayImage from '../../images/dont-overpay.svg';
import secureYourRights from '../../images/secure-your-rights.webp';
import dontGetScammed from '../../images/dont-get-scammed.webp';
import getStellarSupport from '../../images/get-stellar-support.svg';
import targetImage from '../../images/target.svg';
import handStarsImage from '../../images/hand_stars.svg';

import iphoneFrame from '../../images/iphone-15-pro-blue-titanium.svg';
import photoUploadIcon from '../../images/photo_upload.webp';
import uploadIcon from '../../images/upload_icon.svg';
import refreshIcon from '../../images/refresh.svg';
import saveIcon from '../../images/save.svg';
import matchmedicLogoDark from '../../images/matchmedic_logo_dark.png';
import profileMashup from '../../images/profileMashup.png';
import trustpilotBanner from '../../images/trustpilotBanner.png';
import reviewImage1 from '../../images/reviewImages/reviewImage1.webp';
import reviewImage2 from '../../images/reviewImages/reviewImage2.webp';
import reviewImage3 from '../../images/reviewImages/reviewImage3.webp';
import reviewImage4 from '../../images/reviewImages/reviewImage4.webp';
import reviewImage5 from '../../images/reviewImages/reviewImage5.webp';
import alpImage from '../../images/alp_image.webp';
import enesImage from '../../images/enes_image.webp';
import facebookMessengerLogo from '../../images/facebookMessengerLogo.png';
import iMessageLogo from '../../images/iMessageLogo.png';
import whatsAppLogo from '../../images/WhatsAppLogo.png';
import emailLogo from '../../images/emailLogo.svg';
import checkmarkIcon from '../../images/checkmarkIcon.svg';
import glassCardDisplacementMap from '../../images/displacement/test10.png';
import glassTileDisplacementMap from '../../images/displacement/square.png';
import likeIcon from '../../images/optionIcons/likeIcon.svg';
import dislikeIcon from '../../images/optionIcons/dislikeIcon.svg';

// Medical Background Images

import heartAttackIcon from '../../images/medical_background_images/heart-attack.png';
import peanutFreeIcon from '../../images/medical_background_images/peanut-free.png';
import diabetesIcon from '../../images/medical_background_images/diabetes.png';
import pillMedicineIcon from '../../images/medical_background_images/pill-medicine.png';
import virusIcon from '../../images/medical_background_images/virus.png';
import scarHandIcon from '../../images/medical_background_images/scar-hand.png';
import syringeIcon from '../../images/medical_background_images/syringe.png';
import bandAidIcon from '../../images/medical_background_images/band-aid.png';
import smokingIcon from '../../images/medical_background_images/smoking.png';
import drugBottleIcon from '../../images/medical_background_images/drug-bottle.png';
import scissorsIcon from '../../images/medical_background_images/scissors.png';
import sleepingMaskIcon from '../../images/medical_background_images/sleeping-mask.png';
import bloodCellIcon from '../../images/medical_background_images/blood-cell.png';
import thumbsUpIcon from '../../images/medical_background_images/thumbs-up.png';

// Country Icons

import usaIcon from '../../images/flags/USA.png';
import ukIcon from '../../images/flags/UK.png';
import caIcon from '../../images/flags/CAN.png';
import auIcon from '../../images/flags/AUS.png';
import deIcon from '../../images/flags/DEU.png';
import frIcon from '../../images/flags/FRA.png';
import fiIcon from '../../images/flags/FIN.png';
import seIcon from '../../images/flags/SWE.png';
import dkIcon from '../../images/flags/DEN.png';
import esIcon from '../../images/flags/ESP.png';
import itIcon from '../../images/flags/ITA.png';
import otherIcon from '../../images/flags/other.svg';
// import otherIconLight from '../../images/flags/other_light.svg';

// Option Icons
import numberOne from '../../images/optionIcons/number-1.svg';
import numberTwo from '../../images/optionIcons/number-2.svg';
import numberThree from '../../images/optionIcons/number-3.svg';
import numberFour from '../../images/optionIcons/number-4.svg';

// Optional route images for Norwood scale (when skipping image analysis)
import NW1 from '../../images/optionalRouteImages/NW1.png';
import NW2 from '../../images/optionalRouteImages/NW2.png';
import NW3 from '../../images/optionalRouteImages/NW3.png';
import NW4 from '../../images/optionalRouteImages/NW4.png';
import NW5 from '../../images/optionalRouteImages/NW5.png';
import NW6 from '../../images/optionalRouteImages/NW6.png';
import NW7 from '../../images/optionalRouteImages/NW7.png';

// Optional route images for hair type and hair color (manual path)
import StraightHair from '../../images/optionalRouteImages/Straight.svg';
import WavyHair from '../../images/optionalRouteImages/Wavy.svg';
import CurlyHair from '../../images/optionalRouteImages/Curly.svg';

// When did you first notice your hair loss?

import over5Years from '../../images/optionIcons/over-5-years.svg';
import within5Years from '../../images/optionIcons/within-5-years.svg';
import withinLastYear from '../../images/optionIcons/within-last-year.svg';

// Without hair loss in the past [X years], how different would life be?

import muchBetter from '../../images/optionIcons/much-better.svg';
import aboutTheSame from '../../images/optionIcons/about-the-same.svg';
import muchWorse from '../../images/optionIcons/much-worse.svg';

// How much do you think about hair loss each day?

import moreThanOnce from '../../images/optionIcons/more-than-once.svg';
import once from '../../images/optionIcons/once.svg';
import none from '../../images/optionIcons/none.svg';

// Do people act differently towards you due to your hair loss?

import yes from '../../images/optionIcons/yes.svg';
import no from '../../images/optionIcons/no.svg';

// How stressed are you because of hair loss?

import highlyStressed from '../../images/optionIcons/highly-stressed.svg';
import occasionallyStressed from '../../images/optionIcons/occasionally-stressed.svg';
import notStressed from '../../images/optionIcons/not-stressed-at-all.svg';

// What have you done so far to combat hair loss?

import hadHairTransplant from '../../images/optionIcons/had-a-hair-transplant.svg';
import usedMedication from '../../images/optionIcons/used-medication.svg';
import nothing from '../../images/optionIcons/nothing.svg';

// Can you travel to Turkey for the procedure?

//yes already declared
import ifRequired from '../../images/optionIcons/if-required.svg';
//no already declared

// Ideally, when do you want to get the operation?
import lowTimeLeft from '../../images/optionIcons/low_time_left.svg';
import mediumTimeLeft from '../../images/optionIcons/medium_time_left.svg';
import highTimeLeft from '../../images/optionIcons/high_time_left.svg';

// What's the most you can spend on the procedure?

import lessThan4000 from '../../images/optionIcons/less-than-4000.svg';
import between4000to8000 from '../../images/optionIcons/between-4000-to-8000.svg';
import moreThan8000 from '../../images/optionIcons/more-than-8000.svg';

// Did you have a hair transplant before?

//yes already declared
//no already declared

// What do you find important in a clinic?

import highSurgeonInvolvement from '../../images/optionIcons/high-surgeon-involvement.svg';
import onePatientPerDayPolicy from '../../images/optionIcons/one-patient-per-day-policy.svg';
import videoCallWithDoctor from '../../images/optionIcons/video-call-with-doctor.svg';

// Are you okay with using medication?

//yes already declared
//if required already declared
//no already declared

const TINTABLE_SVG_ASSETS = [
  otherIcon,
  over5Years,
  within5Years,
  withinLastYear,
  muchBetter,
  aboutTheSame,
  muchWorse,
  moreThanOnce,
  once,
  none,
  yes,
  no,
  highlyStressed,
  occasionallyStressed,
  notStressed,
  hadHairTransplant,
  usedMedication,
  nothing,
  ifRequired,
  lowTimeLeft,
  mediumTimeLeft,
  highTimeLeft,
  lessThan4000,
  between4000to8000,
  moreThan8000,
  highSurgeonInvolvement,
  onePatientPerDayPolicy,
  videoCallWithDoctor,
  likeIcon,
  dislikeIcon,
  checkmarkIcon,
  StraightHair,
  WavyHair,
  CurlyHair,
];

TINTABLE_SVG_ASSETS.forEach(registerTintableSvg);

/**
 * ONBOARDING PAGE CONTROLLER
 * 
 * This is the main controller for the onboarding flow. It manages:
 * - Current step navigation
 * - All user responses/data collection
 * - Flow control and routing between slides
 * - Slide transition animations and direction
 * 
 * HOW TO USE:
 * 1. Add new slides to the renderSlide() function
 * 2. Update TOTAL_STEPS when adding/removing slides
 * 3. Store user responses in the onboardingState store
 * 4. Handle special navigation rules in goNext()/goBack()
 * 
 * ADDING NEW SLIDES:
 * - Import your slide component
 * - Add a case in renderSlide()
 * - Pass standard props: title, subtitle, currentStep, totalSteps, onNext, onBack
 * - Pass any slide-specific props and data
 */

const TOTAL_STEPS = 30;
// Firestore updates run every 2 seconds to avoid missing user responses.
const SYNC_INTERVAL_MS = 2000;
const SESSION_STORAGE_KEY = 'mm_onboarding_state_v1';
const PROGRESS_STORAGE_KEY = 'mm_onboarding_progress_v1';
const IMAGE_SKIP_FLAG_KEY = 'mm_onboarding_skip_extra_slides_v1';
const APPEND_ONLY_FIELDS = new Set([
  'sessionIds',
  'selfieStorageLocations',
  'selfieAnalysisSummaries',
  'selfieHairgenResults',
  'aestheticPreferenceLikes',
  'aestheticPreferenceDislikes',
  'userNavLog',
  'faultyUploads',
  'saveContacts',
  'contactButtonClicks',
  'welcomeEmailAttempts',
]);

const PRIORITY_ASSETS = [
  reclaimConfidenceImage,
  avoidHorrorStoriesImage,
  dontOverpayImage,
  secureYourRights,
  dontGetScammed,
  getStellarSupport,
  iphoneFrame,
  profileMashup,
  photoUploadIcon,
  uploadIcon,
  refreshIcon,
  saveIcon,
  matchmedicLogoDark,
  usaIcon,
  heartAttackIcon,
  peanutFreeIcon,
  diabetesIcon,
  pillMedicineIcon,
  virusIcon,
  scarHandIcon,
  syringeIcon,
  bandAidIcon,
  smokingIcon,
  drugBottleIcon,
  scissorsIcon,
  sleepingMaskIcon,
  bloodCellIcon,
  thumbsUpIcon,
];

// NEW FLOW step mapping for asset preloading:
// 0: Cover, 1: Birthdate, 2: HairProblem, 3: HairLossOnset, 4: Name,
// 5: Upload, 5.1-5.4: Optional path, 5.5: Analysis,
// 6: ThoughtFrequency, 7: ActionsTaken, 8: StressLevel, 9: RedDisplay,
// 10: GettingHairBack, 11: ImageGen, 12+: rest of flow
const STEP_ASSET_MANIFEST = {
  // Step 0: CoverSlide
  0: [iphoneFrame],
  // Step 1: Birthdate
  1: [],
  // Step 2: HairProblem (NEW)
  2: [],
  // Step 3: HairLossOnset
  3: [over5Years, within5Years, withinLastYear],
  // Step 4: NameInput
  4: [],
  // Step 5: UploadSlide
  5: [photoUploadIcon, uploadIcon, refreshIcon, saveIcon, matchmedicLogoDark],
  // Step 5.1-5.4: Optional path
  5.1: [NW1, NW2, NW3, NW4, NW5, NW6, NW7],
  5.2: [],
  5.3: [StraightHair, WavyHair, CurlyHair],
  5.4: [],
  // Step 6: ThoughtFrequency
  6: [moreThanOnce, once, none],
  // Step 7: ActionsTaken
  7: [hadHairTransplant, usedMedication, nothing],
  // Step 8: StressLevel
  8: [highlyStressed, occasionallyStressed, notStressed],
  // Step 9: RedDisplay
  9: [checkmarkIcon],
  // Step 10: GettingHairBack
  10: [handStarsImage],
  // Step 11: ImageGen
  11: [],
  // Step 12: Country
  12: [usaIcon, ukIcon, caIcon, auIcon, deIcon, frIcon, fiIcon, seIcon, dkIcon, esIcon, itIcon, otherIcon],
  // Step 13: Travel
  13: [yes, ifRequired, no],
  // Step 14: Timeline
  14: [lowTimeLeft, mediumTimeLeft, highTimeLeft],
  // Step 15: Budget
  15: [lessThan4000, between4000to8000, moreThan8000],
  // Step 16: Budget Display
  16: [targetImage],
  // Step 17: TikTokEmbed
  17: [iphoneFrame, profileMashup],
  // Step 18: AestheticGoals
  18: [getStellarSupport, likeIcon, dislikeIcon],
  // Step 19: GoodTaste
  19: [checkmarkIcon],
  // Step 20: MedicalBackground
  20: [heartAttackIcon, peanutFreeIcon, diabetesIcon, pillMedicineIcon, virusIcon, scarHandIcon, syringeIcon, bandAidIcon, smokingIcon, drugBottleIcon, scissorsIcon, sleepingMaskIcon, bloodCellIcon, thumbsUpIcon],
  // Step 21: ReviewsIntro
  21: [trustpilotBanner, profileMashup, reviewImage1, reviewImage2, reviewImage3, reviewImage4, reviewImage5],
  // Step 22: DontOverpay
  22: [dontOverpayImage],
  // Step 23: SecureRights
  23: [secureYourRights],
  // Step 24: DontGetScammed
  24: [dontGetScammed],
  // Step 25: ReliableSupport
  25: [getStellarSupport],
  // Step 26: TeamDisplay
  26: [alpImage, enesImage],
  // Step 27: Conversion
  27: [getStellarSupport, glassCardDisplacementMap, glassTileDisplacementMap],
  // Step 28: ContactInfo
  28: [facebookMessengerLogo, iMessageLogo, whatsAppLogo, emailLogo, checkmarkIcon],
  // Step 29: FinalConversion
  29: [checkmarkIcon],
};


function loadPersistedOnboardingState() {
  if (typeof window === 'undefined') {
    return createInitialOnboardingState();
  }

  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return createInitialOnboardingState();
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return createInitialOnboardingState();
    }

    const base = createInitialOnboardingState({
      createdAt: parsed.session?.createdAt,
      sessionId: parsed.session?.id,
    });

    const mergedFields = parsed.fields && typeof parsed.fields === 'object' ? parsed.fields : {};
    const mergedLatestValues = parsed.latestValues && typeof parsed.latestValues === 'object' ? parsed.latestValues : {};
    const mergedDirtyFields = parsed.dirtyFields && typeof parsed.dirtyFields === 'object' ? parsed.dirtyFields : {};
    const mergedSync = parsed.sync && typeof parsed.sync === 'object' ? parsed.sync : {};

    return {
      ...base,
      session: {
        ...base.session,
        ...(parsed.session || {}),
      },
      fields: {
        ...base.fields,
        ...mergedFields,
      },
      latestValues: {
        ...base.latestValues,
        ...mergedLatestValues,
      },
      dirtyFields: mergedDirtyFields,
      hasPendingChanges: Boolean(parsed.hasPendingChanges),
      sync: {
        ...base.sync,
        ...mergedSync,
      },
    };
  } catch (error) {
    console.error('[OnboardingStorage] Failed to load onboarding state from sessionStorage', error);
    return createInitialOnboardingState();
  }
}

function loadPersistedProgress() {
  if (typeof window === 'undefined') {
    return 0;
  }
  try {
    const raw = window.sessionStorage.getItem(PROGRESS_STORAGE_KEY);
    const parsed = Number(raw);
    if (Number.isInteger(parsed) && parsed >= 0 && parsed < TOTAL_STEPS) {
      return parsed;
    }
  } catch (error) {
    console.error('[OnboardingStorage] Failed to load onboarding progress from sessionStorage', error);
  }
  return 0;
}

function persistProgress(step) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const normalized = Number(step);
    if (!Number.isInteger(normalized)) {
      return;
    }
    const clamped = Math.max(0, Math.min(TOTAL_STEPS - 1, normalized));
    window.sessionStorage.setItem(PROGRESS_STORAGE_KEY, String(clamped));
  } catch (error) {
    console.error('[OnboardingStorage] Failed to persist onboarding progress to sessionStorage', error);
  }
}

function persistOnboardingState(state) {
  if (typeof window === 'undefined' || !state) {
    return;
  }
  try {
    const payload = {
      session: state.session,
      fields: state.fields,
      latestValues: state.latestValues,
      dirtyFields: state.dirtyFields || {},
      hasPendingChanges: Boolean(state.hasPendingChanges),
      sync: state.sync,
    };
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('[OnboardingStorage] Failed to persist onboarding state to sessionStorage', error);
  }
}

function getInitialImageSkipPreference() {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.sessionStorage.getItem(IMAGE_SKIP_FLAG_KEY) === 'true';
}

function persistImageSkipPreference(flag) {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.setItem(IMAGE_SKIP_FLAG_KEY, flag ? 'true' : 'false');
}

function shouldSkipStepForFlag(step, skipFlag) {
  // Optional path slides (5.1-5.4: ManualNorwood, SkinType, HairType, HairColor)
  // These are shown when skipFlag is TRUE (user skipped image upload)
  // They are skipped when skipFlag is FALSE (user uploaded image and went through analysis)
  return !skipFlag && step >= 5.1 && step <= 5.4;
}

// Helper: Determine if a country requires a visa to Turkey
function determineVisaRequirement(countrySelection) {
  if (!countrySelection) return null;

  // Countries from option buttons that do NOT require visa
  const noVisaCountries = ['us', 'uk', 'de', 'fr', 'fi', 'se', 'dk', 'es', 'it'];
  
  // Countries from option buttons that DO require visa
  const visaRequiredCountries = ['ca', 'au'];

  // Check if it's one of the main option buttons
  if (noVisaCountries.includes(countrySelection)) {
    return false; // Visa NOT required
  }
  
  if (visaRequiredCountries.includes(countrySelection)) {
    return true; // Visa required
  }

  // For countries from "other" dropdown, we need to check the countries.json
  // The actual country name is stored, not the ID
  // We'll handle this in the component by looking up the country in the JSON
  return null; // Will be determined from countries.json lookup
}

// Helper: Check if step 7.5 (visa question) should be shown
function shouldShowVisaSlide(onboardingValues) {
  const country = onboardingValues?.countryOfResidence;
  const visaNotRequired = onboardingValues?.countryVisaNotRequired;
  
  // If we have explicit visa requirement info, use it
  if (typeof visaNotRequired === 'boolean') {
    return !visaNotRequired; // Show slide if visa IS required
  }
  
  // Fallback: determine from country selection
  const requirement = determineVisaRequirement(country);
  if (requirement !== null) {
    return requirement; // Show if visa required
  }
  
  return false; // Default: don't show
}

function computeNextStepForFlag(step, skipFlag, onboardingValues = {}) {
  // NEW FLOW (steps 0-29):
  // 0 (Cover) -> 1 (Birthdate) -> 2 (HairProblem) -> 3 (HairLossOnset) -> 4 (Name) -> 5 (Upload)
  // From 5 (Upload):
  //   - If skip: 5 -> 5.1 -> 5.2 -> 5.3 -> 5.4 -> 6 (optional path)
  //   - If upload: 5 -> 5.5 -> 6 (main path, 5.5 is ONE-WAY)
  // 6 -> 7 -> 8 -> 9 -> 10
  // From 10:
  //   - If !skipFlag: 10 -> 11 (ImageGen) -> 12
  //   - If skipFlag: 10 -> 12 (skip ImageGen)
  // 12 -> ... -> 27 -> 28 -> 28.5 (optional email) -> 29
  
  // Step 0 (CoverSlide) -> Birthdate
  if (step === 0) {
    return 1;
  }
  
  // Steps 1-4: sequential main path
  if (step >= 1 && step <= 4) {
    return step + 1;
  }
  
  // Step 5 (UploadSlide)
  if (step === 5) {
    if (skipFlag) {
      return 5.1; // Skipped: go to ManualNorwood (optional path)
    } else {
      return 5.5; // Upload -> Analysis (main path)
    }
  }
  
  // Step 5.5 (ImageAnalysis) -> ThoughtFrequency (step 6)
  if (step === 5.5) {
    return 6;
  }
  
  // Optional path: 5.1 -> 5.2 -> 5.3 -> 5.4 -> 6
  if (step === 5.1) return 5.2;
  if (step === 5.2) return 5.3;
  if (step === 5.3) return 5.4;
  if (step === 5.4) return 6; // End of optional path -> ThoughtFrequency
  
  // Steps 6-8: sequential
  if (step >= 6 && step <= 8) {
    return step + 1;
  }
  
  // Step 9 (RedDisplaySlide) -> Step 10 ("Getting your hair back")
  if (step === 9) {
    return 10;
  }
  
  // Step 10 ("Getting your hair back") -> ImageGen or next section
  if (step === 10) {
    if (skipFlag) {
      return 12; // Skipped: skip ImageGen, go to next section
    } else {
      return 11; // Main path: go to ImageGen
    }
  }
  
  // Step 11 (ImageGen) -> Next section (step 12)
  if (step === 11) {
    return 12;
  }
  
  // Steps 12-27: sequential
  if (step >= 12 && step <= 27) {
    return step + 1;
  }
  
  // Step 28 (ContactInfo) - handled by goNext with custom logic
  // Step 28.5 (EmailSlide) -> FinalConversion
  if (step === 28.5) {
    return 29; // Email -> FinalConversion
  }
  
  // Step 29 (FinalConversion) - last step
  if (step === 29) {
    return 29; // Stay on final
  }
  
  return step + 1;
}

function computePreviousStepForFlag(step, skipFlag, onboardingValues = {}) {
  // NEW FLOW backward navigation:
  // CRITICAL: ImageAnalysisSlide (5.5) is ONE-WAY - always skip it when going back
  
  // Step 0 (Cover) - can't go back further
  if (step === 0) {
    return 0;
  }
  
  // Steps 1-4: sequential backward
  if (step >= 1 && step <= 4) {
    return step - 1;
  }
  
  // Step 5 (Upload) -> Name (step 4)
  if (step === 5) {
    return 4;
  }
  
  // Step 5.5 (Analysis) -> Upload (5)
  if (step === 5.5) {
    return 5;
  }
  
  // Step 5.1 (ManualNorwood - start of optional path) -> Upload (5)
  if (step === 5.1) {
    return 5;
  }
  
  // Steps 5.2-5.4 (optional path) -> previous optional path step
  if (step === 5.2) return 5.1;
  if (step === 5.3) return 5.2;
  if (step === 5.4) return 5.3;
  
  // Step 6 (ThoughtFrequency) -> depends on path taken
  if (step === 6) {
    if (skipFlag) {
      return 5.4; // Went through optional path, go back to ManualHairColor
    } else {
      return 5; // Went through Analysis, skip 5.5 (one-way), go to Upload
    }
  }
  
  // Steps 7-9 -> previous step
  if (step >= 7 && step <= 9) {
    return step - 1;
  }
  
  // Step 10 ("Getting your hair back") -> RedDisplaySlide (9)
  if (step === 10) {
    return 9;
  }
  
  // Step 11 (ImageGen) -> Step 10
  if (step === 11) {
    return 10;
  }
  
  // Step 12 -> depends on path taken
  if (step === 12) {
    if (skipFlag) {
      return 10; // Skipped ImageGen, go back to "Getting your hair back"
    } else {
      return 11; // Main path, go back to ImageGen
    }
  }
  
  // Steps 13-28 -> previous step (integer steps)
  if (step >= 13 && step <= 28) {
    return step - 1;
  }
  
  // Step 28.5 (EmailSlide) -> ContactInfo (28)
  if (step === 28.5) {
    return 28;
  }
  
  // Step 29 (FinalConversion) -> ContactInfo (skip optional email slide)
  if (step === 29) {
    return 28;
  }
  
  // Fallback: go to step 0
  return 0;
}

function normalizeStepForFlag(step, skipFlag) {
  // ImageAnalysisSlide (5.5) is one-way - if user refreshes on 5.5, go to 5
  if (step === 5.5) {
    return 5;
  }
  
  // If on optional path steps but didn't skip, normalize to step 6
  if (!skipFlag && step >= 5.1 && step <= 5.4) {
    return 6;
  }
  
  return step;
}

export default function OnboardingPage() {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateViewportMetrics = () => {
      const viewport = window.visualViewport;
      const height = viewport ? viewport.height : window.innerHeight;
      document.documentElement.style.setProperty('--app-viewport-height', `${height}px`);

      const bottomInset = viewport
        ? Math.max(0, window.innerHeight - (viewport.height + viewport.offsetTop))
        : 0;
      document.documentElement.style.setProperty('--app-safe-bottom', `${bottomInset}px`);
    };

    updateViewportMetrics();

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', updateViewportMetrics);
    window.addEventListener('resize', updateViewportMetrics);

    return () => {
      viewport?.removeEventListener('resize', updateViewportMetrics);
      window.removeEventListener('resize', updateViewportMetrics);
    };
  }, []);

  // Navigation state
  const [showImageSkipSlides, setShowImageSkipSlides] = useState(() => getInitialImageSkipPreference());
  const [currentStep, setCurrentStep] = useState(() =>
    normalizeStepForFlag(loadPersistedProgress(), getInitialImageSkipPreference())
  );
  const [animationDirection, setAnimationDirection] = useState('forward');
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // All user responses are stored here
  // This will be synced to backend in the future
  const [onboardingState, setOnboardingState] = useState(() => loadPersistedOnboardingState());
  const latestOnboardingValues = onboardingState.latestValues;

  // ----- Tracking integration state -----
  const visitedStepsRef = useRef(new Set());
  const hasSentStartedRef = useRef(false);
  const hasSentReachedContactRef = useRef(false);
  const hasSentCompletedRef = useRef(false);
  const hasSentContactCheckpointRef = useRef(false);
  const hasSentNameCheckpointRef = useRef(false);

  // Utility: stable step keys for analytics (do not rely on dynamic titles)
  // NEW FLOW (steps 0-29):
  // 0: cover, 1: birthdate, 2: hair_problem, 3: hair_loss_onset, 4: name_input,
  // 5: image_upload, 5.1-5.4: optional path, 5.5: image_analysis,
  // 6: thought_frequency, 7: actions_taken, 8: stress_level, 9: red_display,
  // 10: getting_hair_back, 11: image_gen, 12: country, 13: travel_turkey,
  // 14: timeline, 15: budget, 16: budget_display, 17: tiktok_embed,
  // 18: aesthetic_goals, 19: good_taste, 20: medical_background, 21: reviews_intro,
  // 22: dont_overpay, 23: secure_rights, 24: dont_get_scammed, 25: reliable_support,
  // 26: team_display, 27: conversion, 28: contact_info, 28.5: email, 29: final_conversion
  const STEP_KEYS = useMemo(() => ([
    'cover',                // 0
    'birthdate',            // 1
    'hair_problem',         // 2 (NEW)
    'hair_loss_onset',      // 3
    'name_input',           // 4
    'image_upload',         // 5
    'thought_frequency',    // 6
    'actions_taken',        // 7
    'stress_level',         // 8
    'red_display',          // 9
    'getting_hair_back',    // 10
    'image_gen',            // 11
    'country',              // 12
    'travel_turkey',        // 13
    'timeline',             // 14
    'budget',               // 15
    'budget_display',       // 16
    'tiktok_embed',         // 17
    'aesthetic_goals',      // 18
    'good_taste',           // 19
    'medical_background',   // 20
    'reviews_intro',        // 21
    'dont_overpay',         // 22
    'secure_rights',        // 23
    'dont_get_scammed',     // 24
    'reliable_support',     // 25
    'team_display',         // 26
    'conversion',           // 27
    'contact_info',         // 28
    'final_conversion'      // 29
  ]), []);

  // Returns a stable key and the numeric index
  const getStepKey = useCallback((idx) => STEP_KEYS[idx] || `step_${idx}`, [STEP_KEYS]);

  const handleSkipPreferenceChange = useCallback((flag) => {
    if (flag === showImageSkipSlides) {
      return;
    }
    persistImageSkipPreference(flag);
    setShowImageSkipSlides(flag);
  }, [showImageSkipSlides]);

  const persistFieldUpdates = useCallback((updates = {}, options = {}) => {
    if (!updates || typeof updates !== 'object' || Object.keys(updates).length === 0) {
      return;
    }
    setOnboardingState((prev) => applyFieldUpdates(prev, updates, options));
  }, [setOnboardingState]);

  const handleAutoSave = useCallback((updates = {}) => {
    if (!updates || typeof updates !== 'object' || Object.keys(updates).length === 0) {
      return;
    }
    persistFieldUpdates(updates, { source: 'auto' });
  }, [persistFieldUpdates]);

  useEffect(() => {
    persistOnboardingState(onboardingState);
  }, [onboardingState]);

  useEffect(() => {
    persistProgress(currentStep);
  }, [currentStep]);

  useEffect(() => {
    if (showImageSkipSlides) {
      return;
    }
    setCurrentStep((prev) => {
      const normalized = normalizeStepForFlag(prev, showImageSkipSlides);
      return normalized === prev ? prev : normalized;
    });
  }, [showImageSkipSlides]);

  // Link tracking session to onboarding, store sessionId in onboarding doc, and emit onboarding_started once
  useEffect(() => {
    (async () => {
      try {
        // Ensure tracking session exists
        await initializeSession();
        const trackingSessionId = (typeof window !== 'undefined' && window.sessionStorage)
          ? window.sessionStorage.getItem('current_session_id')
          : null;

        // Append starting tracking session ID to onboarding document (array)
        if (trackingSessionId) {
          persistFieldUpdates({ sessionIds: [trackingSessionId] }, { source: 'system' });
        }

        // Link tracking -> onboarding (top-level field)
        if (onboardingState?.session?.id) {
          await linkOnboardingToSession(onboardingState.session.id);
        }

        // Fire onboarding_started once per tab
        if (!hasSentStartedRef.current && onboardingState?.session?.id) {
          const flagKey = 'mm_onboarding_tracked_started_v1';
          const already = (typeof window !== 'undefined' && window.sessionStorage)
            ? window.sessionStorage.getItem(flagKey) === '1'
            : false;
          if (!already) {
            const step_index = currentStep;
            const step_key = getStepKey(step_index);
            await trackEvent('onboarding_started', {
              onboarding_id: onboardingState.session.id,
              step_index,
              step_key,
              step_label: step_key,
              tracking_session_id: trackingSessionId || null,
            });
            if (typeof window !== 'undefined' && window.sessionStorage) {
              window.sessionStorage.setItem(flagKey, '1');
            }
            hasSentStartedRef.current = true;
          }
        }
      } catch (e) {
        console.error('[Onboarding Tracking] init error:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track step visited + max step reached
  useEffect(() => {
    (async () => {
      try {
        const step_index = currentStep;
        const step_key = getStepKey(step_index);
        const onboardingId = onboardingState?.session?.id;
        if (!onboardingId && step_index == null) return;

        // Visited per-step de-dupe
        const visitedKey = 'mm_onboarding_visited_steps_v1';
        let visited = [];
        if (typeof window !== 'undefined' && window.sessionStorage) {
          try { visited = JSON.parse(window.sessionStorage.getItem(visitedKey) || '[]'); } catch {}
        }
        const visitedSet = new Set([...(visited || [])]);
        if (!visitedSet.has(step_index)) {
          visitedSet.add(step_index);
          // Persist visited
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem(visitedKey, JSON.stringify(Array.from(visitedSet).sort((a,b)=>a-b)));
          }
          // Track step visited
          await trackEvent('onboarding_step_visited', {
            onboarding_id: onboardingId,
            step_index,
            step_key,
            step_label: step_key,
          });
        }

        // Max step reached
        const maxKey = 'mm_onboarding_max_step_v1';
        let priorMax = -1;
        if (typeof window !== 'undefined' && window.sessionStorage) {
          const raw = window.sessionStorage.getItem(maxKey);
          priorMax = raw != null ? parseInt(raw, 10) : -1;
        }
        if (Number.isFinite(step_index) && step_index > priorMax) {
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem(maxKey, String(step_index));
          }
          // Update top-level max step on tracking session (no event)
          await updateOnboardingMaxStep({
            onboardingId: onboardingId,
            stepIndex: step_index,
            stepKey: step_key,
            stepLabel: step_key,
          });
        }
      } catch (e) {
        console.error('[Onboarding Tracking] step tracking error:', e);
      }
    })();
  }, [currentStep, onboardingState?.session?.id, getStepKey]);

  // User Navigation Log - track step-by-step navigation including backtracks
  // This creates a chronological log of all steps visited: [0, 0.5, 5, 6, 5, 6, 7, ...]
  useEffect(() => {
    if (currentStep == null) return;
    
    // Get the current navigation log (or empty array if not set)
    const currentLog = Array.isArray(latestOnboardingValues.userNavLog) 
      ? latestOnboardingValues.userNavLog 
      : [];
    
    // Check if this step is already the last entry (avoid duplicate on re-render)
    const lastEntry = currentLog.length > 0 ? currentLog[currentLog.length - 1] : null;
    if (lastEntry === currentStep) {
      return; // Already logged this step as the latest
    }
    
    // Append new step to the log
    const updatedLog = [...currentLog, currentStep];
    
    // Update the field via the existing sync system
    persistFieldUpdates({ userNavLog: updatedLog }, { source: 'navigation' });
  }, [currentStep, latestOnboardingValues.userNavLog, persistFieldUpdates]);

  // Reached contact slide checkpoint (step 28) – fire once
  useEffect(() => {
    if (hasSentReachedContactRef.current) return;
    if (currentStep === 28 && onboardingState?.session?.id) {
      hasSentReachedContactRef.current = true;
      void trackEvent('onboarding_checkpoint_reached_contact', {
        onboarding_id: onboardingState.session.id,
        step_index: 28,
        step_key: getStepKey(28),
        step_label: getStepKey(28),
      });
    }
  }, [currentStep, onboardingState?.session?.id, getStepKey]);

  // Contact provided checkpoint – fallback after leaving contact slide (step 28)
  useEffect(() => {
    if (hasSentContactCheckpointRef.current) return;
    if (currentStep > 28 && onboardingState?.session?.id) {
      const dial = latestOnboardingValues?.primaryContactDialCode || '';
      const raw = latestOnboardingValues?.primaryContactPhoneNumber || '';
      let number = raw;
      if (number && dial && !number.startsWith('+') && !number.startsWith(dial)) {
        number = `${dial}${number}`;
      }
      if (number) {
        hasSentContactCheckpointRef.current = true;
        const region = (latestOnboardingValues?.primaryContactPhoneRegion || '').toString().toUpperCase() || null;
        void trackEvent('onboarding_checkpoint_contact', {
          onboarding_id: onboardingState.session.id,
          phone_number: String(number),
          dial_code: dial || null,
          phone_region: region,
        });
      }
    }
  }, [currentStep, latestOnboardingValues?.primaryContactPhoneNumber, latestOnboardingValues?.primaryContactDialCode, latestOnboardingValues?.primaryContactPhoneRegion, onboardingState?.session?.id]);

  // Email provided checkpoint – fallback after leaving email slide (step 28.5)
  useEffect(() => {
    if (hasSentContactCheckpointRef.current) return;
    if (currentStep > 28.5 && onboardingState?.session?.id) {
      const email = (latestOnboardingValues?.primaryContactEmailAddress || '').toString().trim();
      if (email) {
        hasSentContactCheckpointRef.current = true;
        void trackEvent('onboarding_checkpoint_contact', {
          onboarding_id: onboardingState.session.id,
          email_address: email,
          preferred_channel: 'email',
        });
      }
    }
  }, [currentStep, latestOnboardingValues?.primaryContactEmailAddress, onboardingState?.session?.id]);

  // Name provided checkpoint – fallback after leaving name slide (step 4)
  useEffect(() => {
    if (hasSentNameCheckpointRef.current) return;
    if (currentStep > 4 && onboardingState?.session?.id) {
      const fullName = latestOnboardingValues?.fullLegalName;
      if (fullName && typeof fullName === 'string' && fullName.trim().length > 0) {
        hasSentNameCheckpointRef.current = true;
        void trackEvent('onboarding_checkpoint_name', {
          onboarding_id: onboardingState.session.id,
          full_name: fullName,
          step_index: 4,
          step_key: getStepKey(4),
          step_label: getStepKey(4),
        });
      }
    }
  }, [currentStep, latestOnboardingValues?.fullLegalName, onboardingState?.session?.id, getStepKey]);

  // Fallback: if user leaves name slide with a valid name and it wasn't captured yet, capture now
  useEffect(() => {
    if (hasSentNameCheckpointRef.current) return;
    const fullName = latestOnboardingValues?.fullLegalName;
    if (
      currentStep > 4 &&
      fullName && typeof fullName === 'string' && fullName.trim().length > 0 &&
      onboardingState?.session?.id
    ) {
      hasSentNameCheckpointRef.current = true;
      void trackEvent('onboarding_checkpoint_name', {
        onboarding_id: onboardingState.session.id,
        full_name: fullName,
        step_index: 4,
        step_key: getStepKey(4),
        step_label: getStepKey(4),
      });
    }
  }, [currentStep, latestOnboardingValues?.fullLegalName, onboardingState?.session?.id, getStepKey]);

  // Completed checkpoint – first time reaching final slide (29)
  useEffect(() => {
    if (hasSentCompletedRef.current) return;
    if (currentStep === 29 && onboardingState?.session?.id) {
      hasSentCompletedRef.current = true;
      void trackEvent('onboarding_completed', {
        onboarding_id: onboardingState.session.id,
        step_index: 29,
        step_key: getStepKey(29),
        step_label: getStepKey(29),
      });
    }
  }, [currentStep, onboardingState?.session?.id, getStepKey]);

  const contactDialCode = latestOnboardingValues.primaryContactDialCode;
  const contactPhoneNumber = latestOnboardingValues.primaryContactPhoneNumber;
  const contactLocalNumber = useMemo(() => {
    if (!contactPhoneNumber) return '';
    if (contactDialCode && contactPhoneNumber.startsWith(contactDialCode)) {
      return contactPhoneNumber.slice(contactDialCode.length);
    }
    if (contactPhoneNumber && contactPhoneNumber.startsWith('+')) {
      return contactPhoneNumber.slice(1);
    }
    return contactPhoneNumber;
  }, [contactDialCode, contactPhoneNumber]);

  // Determine TikTok folder from skin type.
  // If user skipped upload and went through manual entry, prefer manualSkinType.
  // Otherwise prefer analysis result. This ensures most recent choice is respected.
  const tikTokFolder = useMemo(() => {
    const analysisVal = (latestOnboardingValues.lastAnalysisSkinType || '').toString().trim();
    const manualVal = (latestOnboardingValues.manualSkinType || '').toString().trim();
    // If on skip path (manual entry), prefer manual; else prefer analysis
    const raw = showImageSkipSlides
      ? (manualVal || analysisVal)
      : (analysisVal || manualVal);
    if (!raw) return 'W';
    const norm = raw.toLowerCase();
    // Strip common separators for flexible matching
    const collapsed = norm.replace(/[^a-z]/g, '');
    // Order matters when terms can overlap; check specific before general
    if (collapsed.includes('black')) return 'B';
    if (collapsed.includes('white')) return 'W';
    if (collapsed.includes('indian')) return 'A1';
    if (collapsed.includes('middleeastern') || collapsed.includes('middleeast')) return 'O2';
    if (collapsed.includes('asian')) return 'A4';
    return 'W';
  }, [latestOnboardingValues.lastAnalysisSkinType, latestOnboardingValues.manualSkinType, showImageSkipSlides]);

  // Aesthetic Goals folder mapping (W, B, A1, A4, O)
  // Same logic as tikTokFolder: prefer manual entry when on skip path
  const aestheticFolder = useMemo(() => {
    const analysisVal = (latestOnboardingValues.lastAnalysisSkinType || '').toString().trim();
    const manualVal = (latestOnboardingValues.manualSkinType || '').toString().trim();
    const raw = showImageSkipSlides
      ? (manualVal || analysisVal)
      : (analysisVal || manualVal);
    if (!raw) return 'W';
    const norm = raw.toLowerCase();
    const collapsed = norm.replace(/[^a-z]/g, '');
    if (collapsed.includes('black')) return 'B';
    if (collapsed.includes('white')) return 'W';
    if (collapsed.includes('indian')) return 'A1';
    if (collapsed.includes('middleeastern') || collapsed.includes('middleeast')) return 'O';
    if (collapsed.includes('asian')) return 'A4';
    return 'W';
  }, [latestOnboardingValues.lastAnalysisSkinType, latestOnboardingValues.manualSkinType, showImageSkipSlides]);

  const aestheticImages = useMemo(() => {
    const folder = aestheticFolder || 'W';
    const arr = [];
    let ctx = null;
    try { ctx = require.context('../../images/AestheticGoals', true, /\.webp$/); } catch {}
    for (let i = 1; i <= 7; i += 1) {
      const rel = `./${folder}/${folder}${i}.webp`;
      try {
        const src = ctx ? ctx(rel) : rel;
        arr.push(src);
      } catch {}
    }
    return arr;
  }, [aestheticFolder]);

  // Build a dynamic manifest that includes skin-type specific Aesthetic Goals images only once
  // we know the user's skin type (from analysis or manual entry). This avoids preloading unused assets.
  const hasSkinType = useMemo(() => {
    const a = (latestOnboardingValues.lastAnalysisSkinType || '').toString().trim();
    const m = (latestOnboardingValues.manualSkinType || '').toString().trim();
    return Boolean(a || m);
  }, [latestOnboardingValues.lastAnalysisSkinType, latestOnboardingValues.manualSkinType]);

  const DYNAMIC_MANIFEST = useMemo(() => {
    if (!hasSkinType || !Array.isArray(aestheticImages) || aestheticImages.length === 0) {
      return STEP_ASSET_MANIFEST;
    }
    const merged = { ...STEP_ASSET_MANIFEST };
    // Preload the chosen 7 images on the two slides that use them
    merged[27] = [ ...(STEP_ASSET_MANIFEST[27] || []), ...aestheticImages ];
    merged[28] = [ ...(STEP_ASSET_MANIFEST[28] || []), ...aestheticImages ];
    return merged;
  }, [hasSkinType, aestheticImages]);

  useOnboardingAssetPreloader({
    manifest: DYNAMIC_MANIFEST,
    priorityAssets: PRIORITY_ASSETS,
    currentStep,
    windowAhead: 8,
  });

  // Unified hair-loss severity normalization in [0,1] (analysis preferred, fallback to manual)
  const severityNorm = useMemo(() => {
    try {
      const bucketRaw = (latestOnboardingValues.lastAnalysisBaldnessBucket || '').toString().trim().toLowerCase();
      const bucketMap = { none: 0, minimal: 0.25, moderate: 0.5, severe: 0.75, full_bald: 1 };
      const baldness = Number.isFinite(bucketMap[bucketRaw]) ? bucketMap[bucketRaw] : null;

      const parseManualNW = (val) => {
        if (!val) return null;
        const s = String(val).trim().toLowerCase();
        const roman = s.replace(/^nw[-_\s]?/, '').replace(/[^ivx]/g, '');
        const rmap = { i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6, vii: 7 };
        let n = rmap[roman];
        if (!n) {
          const m = s.match(/([1-7])/);
          if (m) n = parseInt(m[1], 10);
        }
        if (!n || n < 1 || n > 7) return null;
        return (n - 1) / 6;
      };
      const manual = parseManualNW(latestOnboardingValues.manualNorwoodScore);
      if (Number.isFinite(baldness)) return Math.max(0, Math.min(1, baldness));
      if (Number.isFinite(manual)) return Math.max(0, Math.min(1, manual));
      return 0.5; // neutral if unavailable
    } catch {
      return 0.5;
    }
  }, [latestOnboardingValues.lastAnalysisBaldnessBucket, latestOnboardingValues.manualNorwoodScore]);

  // Candidate Score (bar) – base 75 + age modifier + hair-loss modifier
  const candidateScorePercent = useMemo(() => {
    const base = 75;
    const age = Number(latestOnboardingValues.calculatedAgeInYears);
    let ageMod = 0;
    if (Number.isFinite(age)) {
      if (age >= 56) ageMod = 1;
      else if (age >= 46) ageMod = 2;
      else if (age >= 36) ageMod = 3;
      else if (age >= 26) ageMod = 5;
      else if (age >= 18) ageMod = 4;
    }
    // Split [0,1] into 7 equal parts and map to modifiers
    const bucketIndex = Math.min(6, Math.max(0, Math.floor(severityNorm * 7))); // 0..6
    const hairMods = [1, 2, 5, 7, 6, 4, 3];
    const hairMod = hairMods[bucketIndex] || 0;
    const result = Math.max(0, Math.min(100, Math.round(base + ageMod + hairMod)));
    return result;
  }, [latestOnboardingValues.calculatedAgeInYears, severityNorm]);

  // Expected surgery cost derived from budget + severity modifier
  const expectedCost = useMemo(() => {
    const sel = (latestOnboardingValues.budgetRangeForProcedure || '').toString();
    let base = 4500;
    if (sel === 'less-than-4000') base = 3500;
    else if (sel === 'between-4000-and-8000') base = 4500;
    else if (sel === 'more-than-8000') base = 8500;

    let mod = 0;
    if (severityNorm < 0.2) mod = -300;
    else if (severityNorm < 0.3) mod = -200;
    else if (severityNorm < 0.4) mod = -100;
    else if (severityNorm < 0.6) mod = 0;
    else if (severityNorm < 0.7) mod = 100;
    else if (severityNorm < 0.8) mod = 200;
    else mod = 300;

    const value = base + mod;
    const clamped = Math.max(3000, Math.min(10000, value));
    const labelLow = Math.max(3000, clamped - 100);
    const label = `$${labelLow}-${'$'}${clamped}`.replace('-$', '-$');
    return { value: clamped, label };
  }, [latestOnboardingValues.budgetRangeForProcedure, severityNorm]);

  // Medical Background metrics
  const medicalHairLossPercent = useMemo(() => {
    return Math.max(0, Math.min(100, Math.round(severityNorm * 100)));
  }, [severityNorm]);

  const medicalStressPercent = useMemo(() => {
    // Base from step 13
    const stressId = (latestOnboardingValues.hairLossStressLevel || '').toString();
    let base = 60;
    if (stressId === 'highly-stressed') base = 75;
    else if (stressId === 'occassionally-stressed') base = 60; // spelling per ids
    else if (stressId === 'not-stressed') base = 40;

    // Severity modifier
    let sevMod = 0;
    if (severityNorm < 0.3) sevMod = -10;
    else if (severityNorm < 0.4) sevMod = -5;
    else if (severityNorm < 0.6) sevMod = 0;
    else sevMod = 10;

    // Thought frequency modifier from step 11
    const freqId = (latestOnboardingValues.dailyHairLossThoughtFrequency || '').toString();
    let freqMod = 0;
    if (freqId === 'more-than-once') freqMod = 10;
    else if (freqId === 'once') freqMod = 5;
    else if (freqId === 'none') freqMod = -5;

    const val = Math.round(base + sevMod + freqMod);
    return Math.max(0, Math.min(100, val));
  }, [latestOnboardingValues.hairLossStressLevel, latestOnboardingValues.dailyHairLossThoughtFrequency, severityNorm]);

  const medicalSocialImpactPercent = useMemo(() => {
    // Base from step 12
    const socialId = (latestOnboardingValues.perceivedSocialImpactFromHairLoss || '').toString();
    let base = socialId === 'yes' ? 75 : 40;

    // Modifier from step 10 (lifeWithoutHairLossPerception)
    const lifeId = (latestOnboardingValues.lifeWithoutHairLossPerception || '').toString();
    let lifeMod = 0;
    if (lifeId === 'much-better') lifeMod = 10;
    else if (lifeId === 'about-the-same') lifeMod = 0;
    else if (lifeId === 'much-worse') lifeMod = -5;

    // Severity modifier
    let sevMod = 0;
    if (severityNorm < 0.2) sevMod = -5;
    else if (severityNorm < 0.3) sevMod = 0;
    else if (severityNorm < 0.5) sevMod = 5;
    else sevMod = 10;

    const val = Math.round(base + lifeMod + sevMod);
    return Math.max(0, Math.min(100, val));
  }, [latestOnboardingValues.perceivedSocialImpactFromHairLoss, latestOnboardingValues.lifeWithoutHairLossPerception, severityNorm]);

  // Aesthetic Goals percent: base from severity + age modifier
  const aestheticGoalsPercent = useMemo(() => {
    let base;
    if (severityNorm < 0.2) base = 76;
    else if (severityNorm < 0.4) base = 69;
    else base = 63;
    const age = Number(latestOnboardingValues.calculatedAgeInYears);
    let ageMod = 0;
    if (Number.isFinite(age)) {
      if (age < 30) ageMod = 10;
      else if (age <= 50) ageMod = 5;
      else ageMod = 0;
    }
    const val = Math.round(base + ageMod);
    return Math.max(0, Math.min(100, val));
  }, [severityNorm, latestOnboardingValues.calculatedAgeInYears]);

  // Helpers for final card fields
  const formatDisplayName = useCallback((nameRaw, maxChars = 20) => {
    if (!nameRaw) return '';
    const titleCased = String(nameRaw)
      .trim()
      .split(/\s+/)
      .map((part) =>
        part
          .split(/([-’'\-])/)
          .map((token, idx) => {
            // keep separators as-is
            if (token === '-' || token === "'" || token === '’') return token;
            if (!token) return token;
            const first = token.charAt(0).toUpperCase();
            const rest = token.slice(1).toLowerCase();
            return first + rest;
          })
          .join(''),
      )
      .join(' ');
    if (titleCased.length > maxChars) {
      return titleCased.slice(0, maxChars) + '.';
    }
    return titleCased;
  }, []);

  const computeIdSuffix = useCallback((sessionIdRaw) => {
    const sid = (sessionIdRaw || '').toString();
    if (!sid) return '';
    const parts = sid.split('-');
    const suffix = parts[parts.length - 1] || '';
    return suffix.toUpperCase();
  }, []);

  const computeValidUntilPlusMonths = useCallback((months = 4) => {
    const d = new Date();
    // JS auto-rollover handles year transitions
    d.setMonth(d.getMonth() + months);
    const day = d.getDate();
    const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const mon = monthsShort[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${mon} ${year}`;
  }, []);

  // Satisfaction percentage parameters (easy to tweak)
  const TIKTOK_PERCENT_PARAMS = {
    BASE: 80,
    BALDNESS_PENALTY: -7, // multiplied by normalized baldness/NW (0..1)
    AGE_MULTIPLIER: 5,    // multiplied by normalized age (-1..1)
  };

  // Compute dynamic satisfaction percentage using analysis (preferred) or manual + age
  const tikTokSatisfiedPercent = useMemo(() => {
    try {
      // Baldness bucket → [0,1]
      const bucketRaw = (latestOnboardingValues.lastAnalysisBaldnessBucket || '').toString().trim().toLowerCase();
      const bucketMap = { none: 0, minimal: 0.25, moderate: 0.5, severe: 0.75, full_bald: 1 };
      const baldnessNorm = Number.isFinite(bucketMap[bucketRaw]) ? bucketMap[bucketRaw] : null;

      // Manual Norwood score → [0,1]
      const parseManualNW = (val) => {
        if (!val) return null;
        const s = String(val).trim().toLowerCase();
        // Accept ids like "nw-iii" or labels like "NW III" or just a digit 1..7
        const roman = s.replace(/^nw[-_\s]?/, '').replace(/[^ivx]/g, '');
        const rmap = { i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6, vii: 7 };
        let n = rmap[roman];
        if (!n) {
          const m = s.match(/([1-7])/);
          if (m) n = parseInt(m[1], 10);
        }
        if (!n || n < 1 || n > 7) return null;
        return (n - 1) / 6; // normalize to [0,1]
      };
      const manualNWNorm = parseManualNW(latestOnboardingValues.manualNorwoodScore);

      // Choose normalized severity: prefer analysis; else manual
      const severityNorm = (baldnessNorm != null ? baldnessNorm : manualNWNorm);
      if (!Number.isFinite(severityNorm)) throw new Error('No severity available');

      // Age normalization: (30 - age) clamped to [-10,10], then /10 → [-1,1]
      const ageYears = Number(latestOnboardingValues.calculatedAgeInYears);
      let ageNorm = 0;
      if (Number.isFinite(ageYears)) {
        const diff = 30 - ageYears;
        const clamped = Math.max(-10, Math.min(10, diff));
        ageNorm = clamped / 10;
      }

      const { BASE, BALDNESS_PENALTY, AGE_MULTIPLIER } = TIKTOK_PERCENT_PARAMS;
      let pct = BASE + (severityNorm * BALDNESS_PENALTY) + (ageNorm * AGE_MULTIPLIER);
      // Safety clamp to [0,100] then round
      pct = Math.round(Math.max(0, Math.min(100, pct)));
      return pct;
    } catch {
      return 87; // fallback per spec
    }
  }, [latestOnboardingValues.lastAnalysisBaldnessBucket, latestOnboardingValues.manualNorwoodScore, latestOnboardingValues.calculatedAgeInYears]);

  const localFingerprint = useMemo(() => computeLocalFingerprint(onboardingState), [onboardingState]);
  const latestSyncPayload = useMemo(() => buildSyncPayload(onboardingState), [onboardingState]);
  const pendingSyncRef = useRef(null);
  const lastSyncedFingerprintRef = useRef(null);
  const isSyncingRef = useRef(false);
  const lastSyncStartedAtRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const attemptSync = useCallback(async () => {
    const pending = pendingSyncRef.current;
    if (!pending || !pending.fingerprint) {
      return;
    }
    if (pending.fingerprint === lastSyncedFingerprintRef.current) {
      return;
    }
    if (isSyncingRef.current) {
      return;
    }

    const now = Date.now();
    if (now - lastSyncStartedAtRef.current < SYNC_INTERVAL_MS) {
      return;
    }

    const payload = pending.payload;
    if (!payload || !payload.session?.id) {
      return;
    }

    const dirtyFields = Array.isArray(payload.dirtyFields) ? payload.dirtyFields : [];
    if (dirtyFields.length === 0) {
      lastSyncedFingerprintRef.current = pending.fingerprint;
      return;
    }

    const updateFields = {};
    dirtyFields.forEach((fieldKey) => {
      if (Object.prototype.hasOwnProperty.call(payload.fields, fieldKey)) {
        updateFields[fieldKey] = payload.fields[fieldKey];
      }
    });

    if (Object.keys(updateFields).length === 0) {
      lastSyncedFingerprintRef.current = pending.fingerprint;
      if (isMountedRef.current) {
        setOnboardingState((prev) =>
          markFieldsAsSynced(prev, dirtyFields, {
            lastSyncedAt: payload.generatedAt,
            firestoreDocumentId: payload.session.id,
          })
        );
      }
      return;
    }

    lastSyncStartedAtRef.current = now;

    const docRef = doc(db, 'recommendations_form_data', payload.session.id);
    const firestorePatch = {
      sessionMetadata: {
        id: payload.session.id,
        createdAt: payload.session.createdAt ?? payload.generatedAt,
        lastUpdatedAt: payload.session.lastUpdatedAt ?? payload.generatedAt,
      },
      lastSyncedAt: serverTimestamp(),
      updates: arrayUnion({
        timestamp: payload.generatedAt,
        fields: updateFields,
      }),
    };

    Object.entries(updateFields).forEach(([fieldKey, value]) => {
      if (typeof value === 'undefined') {
        return;
      }
      if (APPEND_ONLY_FIELDS.has(fieldKey) && Array.isArray(value) && value.length > 0) {
        firestorePatch[`latestSnapshot.${fieldKey}`] = arrayUnion(...value);
      } else {
        firestorePatch[`latestSnapshot.${fieldKey}`] = value;
      }
    });
    firestorePatch['latestSnapshot.lastUpdatedAt'] = serverTimestamp();

    isSyncingRef.current = true;
    try {
      await setDoc(docRef, firestorePatch, { merge: true });
      lastSyncedFingerprintRef.current = pending.fingerprint;
      if (isMountedRef.current) {
        setOnboardingState((prev) =>
          markFieldsAsSynced(prev, dirtyFields, {
            lastSyncedAt: payload.generatedAt,
            firestoreDocumentId: payload.session.id,
          })
        );
      }
    } catch (error) {
      console.error('[OnboardingSync] Failed to upload onboarding data to Firestore', error);
      if (isMountedRef.current) {
        setOnboardingState((prev) =>
          recordSyncError(prev, error, { lastSyncAttemptAt: new Date().toISOString() })
        );
      }
    } finally {
      isSyncingRef.current = false;
    }
  }, [setOnboardingState]);

  useEffect(() => {
    if (onboardingState.hasPendingChanges && localFingerprint) {
      pendingSyncRef.current = { fingerprint: localFingerprint, payload: latestSyncPayload };
      void attemptSync();
    } else {
      pendingSyncRef.current = null;
    }
  }, [attemptSync, localFingerprint, latestSyncPayload, onboardingState.hasPendingChanges]);

  useEffect(() => {
    const syncTimer = setInterval(() => {
      void attemptSync();
    }, SYNC_INTERVAL_MS);

    return () => {
      clearInterval(syncTimer);
    };
  }, [attemptSync]);

  /**
   * Navigate to next slide
   * Add any slide-specific logic here (skipping, validation, etc.)
   */
  const goNext = (data, options = {}) => {
    // Submit-driven checkpoints (use final submitted values)
    try {
      if (data && typeof data === 'object') {
        // Name checkpoint from NameInputSlide (step 4)
        if (data.fullLegalName && !hasSentNameCheckpointRef.current && onboardingState?.session?.id) {
          hasSentNameCheckpointRef.current = true;
          void trackEvent('onboarding_checkpoint_name', {
            onboarding_id: onboardingState.session.id,
            full_name: String(data.fullLegalName),
            step_index: 4,
            step_key: getStepKey(4),
            step_label: getStepKey(4),
          });
        }

        // Contact checkpoint from ContactInfoSlide (step 28)
        if (data.primaryContactPhoneNumber && !hasSentContactCheckpointRef.current && onboardingState?.session?.id) {
          const dial = data.primaryContactDialCode || '';
          let number = String(data.primaryContactPhoneNumber || '');
          if (number && dial && !number.startsWith('+') && !number.startsWith(dial)) {
            number = `${dial}${number}`;
          }
          const region = (data.primaryContactPhoneRegion || '').toString().toUpperCase() || null;
          hasSentContactCheckpointRef.current = true;
          void trackEvent('onboarding_checkpoint_contact', {
            onboarding_id: onboardingState.session.id,
            phone_number: number,
            dial_code: dial || null,
            phone_region: region,
          });
        }

        // Email checkpoint from EmailSlide (step 27.5)
        if (
          data.primaryContactEmailAddress &&
          !hasSentContactCheckpointRef.current &&
          onboardingState?.session?.id
        ) {
          const email = String(data.primaryContactEmailAddress || '').trim();
          if (email) {
            hasSentContactCheckpointRef.current = true;
            void trackEvent('onboarding_checkpoint_contact', {
              onboarding_id: onboardingState.session.id,
              email_address: email,
              preferred_channel: 'email',
            });
          }
        }
      }
    } catch (e) {
      console.error('[Onboarding Tracking] submit checkpoint error:', e);
    }

    if (data && typeof data === 'object' && Object.keys(data).length > 0) {
      persistFieldUpdates(data, options);
    }

    const imageStatus = options?.imageUploadStatus ?? null;
    let nextSkipPreference = showImageSkipSlides;
    if (imageStatus === 'skipped') {
      nextSkipPreference = true;
    } else if (imageStatus === 'completed') {
      nextSkipPreference = false;
    }

    if (nextSkipPreference !== showImageSkipSlides) {
      handleSkipPreferenceChange(nextSkipPreference);
    }

    // Special case: from contact_info (step 28) with a valid phone number, skip optional email slide (28.5) and go directly to final (29)
    let targetStep;
    if (currentStep === 28 && data && typeof data === 'object' && data.primaryContactPhoneNumber) {
      // Jump over email slide
      targetStep = 29; // final_conversion
    } else {
      targetStep = computeNextStepForFlag(currentStep, nextSkipPreference, latestOnboardingValues);
    }
    
    // Validate target step is a known valid step
    const validSteps = [0, 1, 2, 3, 4, 5, 5.1, 5.2, 5.3, 5.4, 5.5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 28.5, 29];
    if (!validSteps.includes(targetStep)) {
      console.error('[OnboardingPage goNext] Invalid target step:', targetStep, '- staying on current step');
      return;
    }
    
    if (targetStep === currentStep) {
      return;
    }

    setAnimationDirection('forward');
  setIsTransitioning(true);

    const nextStepValue = targetStep;

    setTimeout(() => {
      setCurrentStep(nextStepValue);
    }, 10);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 320);
  };

  /**
   * Navigate to previous slide
   * Add any slide-specific back navigation rules here
   */
  const goBack = () => {
    if (currentStep <= 0) {
      return;
    }

    console.log('[OnboardingPage goBack] currentStep:', currentStep, 'showImageSkipSlides:', showImageSkipSlides);
    let targetStep = computePreviousStepForFlag(currentStep, showImageSkipSlides, latestOnboardingValues);
    console.log('[OnboardingPage goBack] computed targetStep:', targetStep);
    
    // Validate target step is a known valid step
    const validSteps = [0, 1, 2, 3, 4, 5, 5.1, 5.2, 5.3, 5.4, 5.5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 28.5, 29];
    if (!validSteps.includes(targetStep)) {
      console.error('[OnboardingPage goBack] Invalid target step:', targetStep, '- redirecting to 0');
      targetStep = 0;
    }
    
    // Note: Analysis slide (5.5) is one-way - computePreviousStepForFlag already handles skipping it
    // When going back from step 6 (ThoughtFrequency) with !showImageSkipSlides, we go directly to 5 (Upload)
    
    if (targetStep === currentStep) {
      return;
    }

    setAnimationDirection('backward');
    setIsTransitioning(true);

    const previousStepValue = targetStep;

    setTimeout(() => {
      setCurrentStep(previousStepValue);
    }, 10);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 320);
  };

  /**
   * Reset to Upload slide (step 5)
   * Used by ImageAnalysisSlide and ImageGenSlide refresh buttons
   */
  const goToUpload = useCallback(() => {
    setAnimationDirection('backward');
    setIsTransitioning(true);

    setTimeout(() => {
      setCurrentStep(5);
    }, 10);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 320);
  }, []);

  /**
   * SLIDE ROUTER
   * Add new slides here following the same pattern
   * Each slide should be self-contained and use SlideShell internally
   */
  
  // Global footer config (controls visibility + text + links)
  const footerConfig = {
    showFooterSubtext: true,
    footerSubtextText: 'By continuing, you agree to our ',
    privacyUrl: 'https://matchmedic.com/documents/PrivacyNotice',
    termsUrl: 'https://matchmedic.com/documents/TermsConditions',
  };

  const renderSlide = () => {
    switch (currentStep) {

      // ============================================
      // NEW FLOW: Steps 0-11
      // ============================================

      case 0:
        // CoverSlide - First slide, hero with iPhone video
        return (
          <CoverSlide
            title="Welcome"
            subtitle="Let's start out by finding out if you have a hair loss problem in the first place."
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            theme="light"
            showProgress={false}
            nextLabel="Get your confidence back ->"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 1:
        // BirthdateSlide - "How old are you?"
        return (
          <BirthdateSlide
            title="How old are you?"
            subtitle="This will be used to personalize your results."
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            initialValue={latestOnboardingValues.birthdateIso8601 || ''}
            minAge={12}
            maxAge={90}
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 2: {
        // NEW: HairProblemSlide - "What seems to be the problem with your hair?"
        const hairProblemsRaw = latestOnboardingValues.hairProblemsIdentified;
        const selectedProblems = Array.isArray(hairProblemsRaw)
          ? hairProblemsRaw
          : hairProblemsRaw
            ? [hairProblemsRaw]
            : [];

        return (
          <OptionSelectSlide
            title="What problems are you experiencing with your hair?"
            subtitle="Select all that apply."
            options={[
              { id: 'hairLineProblems', label: 'Hairline problems', icon: numberOne },
              { id: 'densityIssues', label: 'Density issues', icon: numberTwo },
              { id: 'visibleBaldSpots', label: 'Visible bald spots', icon: numberThree },
              { id: 'hairFall', label: 'Significant hair fall', icon: numberFour },
            ]}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            fieldName="hairProblemsIdentified"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
            multiselect
            minSelections={1}
            selectedValues={selectedProblems}
          />
        );
      }

      case 3: {
        // HairLossOnset - "When did you first notice this issue/these issues?"
        // Title depends on whether user selected single or multiple hair problems in step 2
        const hairProblemsForTitle = latestOnboardingValues.hairProblemsIdentified;
        const problemCount = Array.isArray(hairProblemsForTitle)
          ? hairProblemsForTitle.length
          : hairProblemsForTitle ? 1 : 0;
        const hairLossOnsetTitle = problemCount > 1
          ? "When did you first notice these issues?"
          : "When did you first notice this issue?";

        return (
          <OptionSelectSlide
            title={hairLossOnsetTitle}
            subtitle=""
            options={[
              { id: 'hair-loss-over-5', label: 'Over 5 years ago', icon: over5Years, tintable: true },
              { id: 'hair-loss-within-5', label: 'Within last 5 years', icon: within5Years, tintable: true },
              { id: 'hair-loss-last-year', label: 'Within last year', icon: withinLastYear, tintable: true },
            ]}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            selectedValue={latestOnboardingValues.hairLossOnsetTiming}
            fieldName="hairLossOnsetTiming"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );
      }

      case 4:
        // NameInputSlide - "What's your name?"
        return (
          <NameInputSlide
            title="What's your name?"
            subtitle=""
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            initialValue={latestOnboardingValues.fullLegalName || ''}
            fieldName="fullLegalName"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 5:
        // UploadSlide - Image upload with skip option
        return (
          <UploadSlide
            title="Lastly, let’s see if you have any hair loss"
            subtitle="Take a selfie and find out."
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            sessionId={onboardingState.session?.id}
            theme="light"
          />
        );

      case 5.1:
        // ManualNorwood - Optional path (shown if user skipped upload)
        return (
          <OptionSelectSlide
            title="Which image represents your hair loss the best?"
            subtitle="This will be used to personalize your results."
            options={[
              { id: 'nw-i', label: 'NW\u00A0I', icon: NW1 },
              { id: 'nw-ii', label: 'NW\u00A0II', icon: NW2 },
              { id: 'nw-iii', label: 'NW\u00A0III', icon: NW3 },
              { id: 'nw-iv', label: 'NW\u00A0IV', icon: NW4 },
              { id: 'nw-v', label: 'NW\u00A0V', icon: NW5 },
              { id: 'nw-vi', label: 'NW\u00A0VI', icon: NW6 },
              { id: 'nw-vii', label: 'NW\u00A0VII', icon: NW7 },
            ]}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            selectedValue={latestOnboardingValues.manualNorwoodScore}
            fieldName="manualNorwoodScore"
            gridLayout
            optionIconContainerClass="w-20 h-20 md:w-24 md:h-24 overflow-hidden"
            optionIconSizeClass="w-full h-full"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 5.2:
        // ManualSkinType - Optional path
        return (
          <SelectDropdownSlide
            title="How would you describe yourself?"
            subtitle="This will be used to personalize your results."
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            options={['White', 'Black', 'Asian', 'Middle Eastern', 'Indian', 'Other']}
            fieldName="manualSkinType"
            initialValue={latestOnboardingValues.manualSkinType || ''}
            label="Select ethnicity"
            placeholder="Select ethnicity"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
            onAutoSave={handleAutoSave}
          />
        );

      case 5.3:
        // ManualHairType - Optional path
        return (
          <OptionSelectSlide
            title="How about your hair type?"
            subtitle="This will be used to personalize your results."
            options={[
              { id: 'straight', label: 'Straight', icon: StraightHair, tintable: true },
              { id: 'wavy', label: 'Wavy', icon: WavyHair, tintable: true },
              { id: 'curly', label: 'Curly', icon: CurlyHair, tintable: true },
            ]}
            optionIconContainerClass="w-12 h-5 md:w-16 md:h-6"
            optionIconSizeClass="w-12 h-5 md:w-16 md:h-6"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            selectedValue={latestOnboardingValues.manualHairType}
            fieldName="manualHairType"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 5.4:
        // ManualHairColor - Optional path (last in optional path)
        return (
          <OptionSelectSlide
            title="What is your hair color?"
            subtitle="This will be used to personalize your results."
            options={[
              { id: 'black', label: 'Black', icon: <span className="inline-block rounded-full" style={{ backgroundColor: '#111111' }} /> },
              { id: 'brown', label: 'Brown', icon: <span className="inline-block rounded-full" style={{ backgroundColor: '#7B4B2A' }} /> },
              { id: 'blonde', label: 'Blonde', icon: <span className="inline-block rounded-full" style={{ backgroundColor: '#E8D38A' }} /> },
              { id: 'red', label: 'Red', icon: <span className="inline-block rounded-full" style={{ backgroundColor: '#B63A2B' }} /> },
              { id: 'gray', label: 'Gray', icon: <span className="inline-block rounded-full" style={{ backgroundColor: '#9CA3AF' }} /> },
            ]}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            selectedValue={latestOnboardingValues.manualHairColor}
            fieldName="manualHairColor"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 5.5:
        // ImageAnalysisSlide - ONE-WAY (main path after upload)
        return (
          <ImageAnalysisSlide
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onReset={goToUpload}
            onAutoSave={handleAutoSave}
            sessionId={onboardingState.session?.id}
            theme="light"
            userName={latestOnboardingValues.fullLegalName || ''}
            userAge={latestOnboardingValues.calculatedAgeInYears || null}
            candidateScorePercent={candidateScorePercent}
            expectedSatisfactionPercent={tikTokSatisfiedPercent}
          />
        );

      case 6:
        // ThoughtFrequency - "How much do you think about hair loss each day?"
        return (
          <OptionSelectSlide
            title="How much do you think about hair loss each day?"
            subtitle=""
            options={[
              { id: 'more-than-once', label: 'More than once', icon: moreThanOnce, tintable: true },
              { id: 'once', label: 'Once', icon: once, tintable: true, iconContainerClass:'w-5 h-5 p-1 flex items-center justify-center', iconIconSizeClass: 'w-8 h-8', },
              { id: 'none', label: 'None', icon: none, tintable: true, iconContainerClass:'w-5 h-5 p-1 flex items-center justify-center', iconIconSizeClass: 'w-8 h-8', },
            ]}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}  
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            selectedValue={latestOnboardingValues.dailyHairLossThoughtFrequency}
            fieldName="dailyHairLossThoughtFrequency"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 7: {
        // ActionsTaken - "What have you done so far to combat hair loss?"
        const actionsTakenRaw = latestOnboardingValues.actionsTakenAgainstHairLoss;
        const selectedActions = Array.isArray(actionsTakenRaw)
          ? actionsTakenRaw
          : actionsTakenRaw
            ? [actionsTakenRaw]
            : [];

        return (
          <OptionSelectSlide
            title="What have you done so far to combat hair loss?"
            subtitle="You can select multiple options."
            options={[
              { id: 'had-hair-transplant', label: 'Had a hair transplant', icon: hadHairTransplant, tintable: true  },
              { id: 'used-medication', label: 'Used medication', icon: usedMedication, tintable: true },
              { id: 'nothing', label: 'Nothing', icon: nothing, tintable: true },
            ]}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            fieldName="actionsTakenAgainstHairLoss"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
            multiselect
            minSelections={1}
            selectedValues={selectedActions}
            exclusiveOptionIds={['nothing']}
          />
        );
      }

      case 8:
        // StressLevel - "How stressed are you because of hair loss?"
        return (
          <OptionSelectSlide
            title="How stressed are you because of hair loss?"
            subtitle=""
            options={[
              { id: 'highly-stressed', label: 'Highly stressed', icon: highlyStressed, tintable: true },
              { id: 'occassionally-stressed', label: 'Occasionally stressed', icon: occasionallyStressed, tintable: true  },
              { id: 'not-stressed', label: 'Not stressed at all', icon: notStressed, tintable: true  },
            ]}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            selectedValue={latestOnboardingValues.hairLossStressLevel}
            fieldName="hairLossStressLevel"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 9:
        // RedDisplaySlide - Two horizontal sub-slides about hair loss
        return (
          <RedDisplaySlide
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 10:
        // "Getting your hair back" display slide with blue background
        return (
          <DisplaySlide
            title="Getting your hair back"
            description="Recovering your hairline is possible. We will now check if a hair transplant is right for you and guide you through the process."
            imageSrc={handStarsImage}
            imageAlt="Hands with stars illustration"
            imageSize="small"
            theme="dark"
            backgroundStyle={{ backgroundColor: '#0472D8' }}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            nextLabel="Check my hair transplant eligibility"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 11:
        // BeforeAfterSlide (main path only, skipped for optional path)
        return (
          <BeforeAfterSlide
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onReset={goToUpload}
            onAutoSave={handleAutoSave}
            sessionId={onboardingState.session?.id}
            userName={latestOnboardingValues.fullLegalName || ''}
            theme="dark"
            candidateScorePercent={candidateScorePercent}
            expectedSatisfactionPercent={tikTokSatisfiedPercent}
          />
        );

      // ============================================
      // NEW FLOW: Steps 12-29 (to be renumbered in next batch)
      // ============================================

      case 12:
        // Country passport slide
        return (
          <OptionSelectSlide
            title="Which country’s passport do you hold?"
            subtitle="Your passport must be valid for at least one year."
            options={[
              { id: 'us', label: 'USA', icon: usaIcon, className: 'step7-country-option' },
              { id: 'uk', label: 'UK', icon: ukIcon, className: 'step7-country-option' },
              { id: 'ca', label: 'Canada', icon: caIcon, className: 'step7-country-option' },
              { id: 'au', label: 'Australia', icon: auIcon, className: 'step7-country-option' },
              { id: 'de', label: 'Germany', icon: deIcon, className: 'step7-country-option' },
              { id: 'fr', label: 'France', icon: frIcon, className: 'step7-country-option' },
              { id: 'fi', label: 'Finland', icon: fiIcon, className: 'step7-country-option' },
              { id: 'se', label: 'Sweden', icon: seIcon, className: 'step7-country-option' },
              { id: 'dk', label: 'Denmark', icon: dkIcon, className: 'step7-country-option' },
              { id: 'es', label: 'Spain', icon: esIcon, className: 'step7-country-option' },
              { id: 'it', label: 'Italy', icon: itIcon, className: 'step7-country-option' },
              { id: 'other', label: 'Other', icon: otherIcon, tintable: true, className: 'step7-country-option' }
            ]}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={(data) => {
              // Custom handler to save visa requirement along with country
              if (data && data.countryOfResidence) {
                const selectedCountry = data.countryOfResidence;
                let visaNotRequired = null;
                
                // Check if it's a button option or from dropdown
                const visaRequirement = determineVisaRequirement(selectedCountry);
                
                if (visaRequirement !== null) {
                  // We can determine from button options
                  visaNotRequired = !visaRequirement;
                } else {
                  // It's from "other" dropdown - look up in countries.json
                  const countryData = countries.find(c => c.name === selectedCountry);
                  if (countryData && typeof countryData.visa_not_required === 'boolean') {
                    visaNotRequired = countryData.visa_not_required;
                  } else {
                    // Default: visa required if not specified
                    visaNotRequired = false;
                  }
                }
                
                // Add visa requirement info to the data
                const enrichedData = {
                  ...data,
                  countryVisaNotRequired: visaNotRequired
                };
                
                goNext(enrichedData);
              } else {
                goNext(data);
              }
            }}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            selectedValue={latestOnboardingValues.countryOfResidence}
            gridLayout={true}
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}

            // NEW: enable "Other" dropdown with countries.json
            fieldName="countryOfResidence"
            otherOptionId="other"
            otherOptionsList={countries}
            otherPlaceholder="Select your country"

            // Enable responsive behavior for short screens
            shortViewportBehavior={{
              enabled: true,
              breakpoint: 580,
              compactGridColumns: 4,
              contentPaddingClass: 'pb-4'
            }}
          />
        );

      case 13:
        // Travel to Turkey slide
        return (
          <OptionSelectSlide
            title="Can you travel to Turkey for the procedure?"
            subtitle="Turkish doctors lead the world at affordable and quality hair transplant treatments."
            options={[
              { id: 'yes', label: 'Yes', icon: yes, tintable: true },
              { id: 'if-required', label: 'If required', icon: ifRequired, tintable: true },
              { id: 'no', label: 'No', icon: no, tintable: true },
            ]}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            selectedValue={latestOnboardingValues.travelAbilityToTurkey}
            fieldName="travelAbilityToTurkey"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 14:
        // Timeline preference slide
        return (
          <OptionSelectSlide
            title="Ideally, when do you want to get the operation?"
            subtitle=""
            options={[
              { id: 'within-1-month', label: 'Within 1 month', icon: lowTimeLeft, tintable: true },
              { id: 'within-3-months', label: 'Within 3 months', icon: mediumTimeLeft, tintable: true },
              { id: 'after-3-months', label: 'After 3 months', icon: highTimeLeft, tintable: true },
            ]}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            selectedValue={latestOnboardingValues.idealOperationTimeline}
            fieldName="idealOperationTimeline"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 15:
        // Budget slide
        return (
          <OptionSelectSlide
            title="What's the most you can spend on the procedure?"
            subtitle={(
              <>
                As of 2026, the average cost was{' '}
                <span className="inline-block rounded-md bg-mm_black px-0.5 text-white">$4,234</span>{' '}
                for hair transplants.
              </>
            )}
            options={[
              { id: 'less-than-4000', label: 'Less than $4,000', icon: lessThan4000, tintable: true },
              { id: 'between-4000-and-8000', label: 'Between $4,000 - $8,000', icon: between4000to8000, tintable: true },
              { id: 'more-than-8000', label: 'More than $8,000', icon: moreThan8000, tintable: true },
            ]}
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            selectedValue={latestOnboardingValues.budgetRangeForProcedure}
            fieldName="budgetRangeForProcedure"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      case 16:
        // Budget + Timeline display slide (new display with target.svg)
        return (
          <DisplaySlide
            title="You have a realistic budget and timeline"
            description="Given your current hair loss level, you have a realistic budget and timeline for hair restoration."
            imageSrc={targetImage}
            imageAlt="Target illustration"
            imageSize="small"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            nextLabel="Continue"
            theme="dark"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      // Case 17: TikTokEmbedSlide
      case 17:
        return (
          <TikTokEmbedSlide
            title="100+ men with similar hair loss to you reclaimed their hairline."
            subtitle="On average they had similar hair loss patterns, physical attributes and budget to you."
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            nextLabel="Continue"
            theme="light"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
            storagePrefix={`tiktok/${tikTokFolder}`}
          />
        );

      // Case 18: AestheticGoalsSlide
      case 18:
        return (
          <AestheticGoalsSlide
            title="Tell us about your aesthetic goals."
            subtitle='Which hairlines do you like the most?'
            description=""
            imageSrc={getStellarSupport}
            imageAlt="Aesthetic goals illustration"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            initialLikes={latestOnboardingValues.aestheticPreferenceLikes || []}
            initialDislikes={latestOnboardingValues.aestheticPreferenceDislikes || []}
            nextLabel="Continue"
            theme="light"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
            imagesFolder={aestheticFolder}
          />
        );

      // Case 19: GoodTasteDisplaySlide
      case 19:
        return (
          <GoodTasteDisplaySlide
            title="You have good taste."
            subtitle="We noted your preferences about hairline design and density."
            theme="dark"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            nextLabel="Continue"
            showPatientFileDeclaration={true}
            patientFileDynamicText="your aesthetic goals"
            images={aestheticImages}
            likedImages={(latestOnboardingValues.aestheticPreferenceLikes || []).slice()}
            dislikedImages={(latestOnboardingValues.aestheticPreferenceDislikes || []).slice()}
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      // Case 20: MedicalBackgroundSlide
      case 20:
        {
          const initialMedicalAnswers = {
            genetic_heart_conditions: latestOnboardingValues.genetic_heart_conditions,
            allergies: latestOnboardingValues.allergies,
            chronic_illness: latestOnboardingValues.chronic_illness,
            regular_medication: latestOnboardingValues.regular_medication,
            contagious_disease: latestOnboardingValues.contagious_disease,
            keloid_scars: latestOnboardingValues.keloid_scars,
            needle_phobia: latestOnboardingValues.needle_phobia,
            low_pain_threshold: latestOnboardingValues.low_pain_threshold,
            smoker: latestOnboardingValues.smoker,
            drug_addiction: latestOnboardingValues.drug_addiction,
            previous_surgery: latestOnboardingValues.previous_surgery,
            recent_anesthesia: latestOnboardingValues.recent_anesthesia,
            anemia_diagnosis: latestOnboardingValues.anemia_diagnosis,
          };

          return (
            <MedicalBackgroundSlide
              title="Medical background"
              subtitle="Required information for your safety."
              currentStep={currentStep}
              totalSteps={TOTAL_STEPS}
              onNext={goNext}
              onBack={goBack}
              onAutoSave={handleAutoSave}
              initialAnswers={initialMedicalAnswers}
              theme="light"
              animationDirection={animationDirection}
              isTransitioning={isTransitioning}
            />
          );
        }
  
      // Case 21: ReviewsIntroSlide
      case 21:
        return (
          <ReviewsIntroSlide
            title=""
            description=""
            imageSrc={getStellarSupport}
            imageAlt="Reviews intro illustration"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            nextLabel="Continue"
            theme="dark"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      // Case 22: Don't overpay DisplaySlide
      case 22:
        return (
          <DisplaySlide
            title="Don't overpay"
            description="With us, you will always get the best price possible for your operation. Our service is completely free for our users. We will ensure you will never pay a cent more than absolutely required."
            imageSrc={dontOverpayImage}
            imageAlt="Don't overpay illustration"
            imageSize="small"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            nextLabel="Continue"
            theme="dark"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      // Case 23: Secure your rights DisplaySlide
      case 23:
        return (
          <DisplaySlide
            title="Secure your rights"
            description="Every day, we're on the ground visiting our partnered clinics directly. We make sure our users get the attention they deserve due to our close proximity with the doctors."
            imageSrc={secureYourRights}
            imageAlt="Secure your rights illustration"
            imageSize="small"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            nextLabel="Continue"
            theme="dark"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      // Case 24: Don't get scammed DisplaySlide
      case 24:
        return (
          <DisplaySlide
            title="Don't get scammed"
            description="We verify the credentials of our partnered clinics via official authorities like the Turkish Ministry of Health. We also consider independent patient reviews to ensure clinic professionalism in practice."
            imageSrc={dontGetScammed}
            imageAlt="Don't get scammed illustration"
            contentContainerClassName="mt-24 md:mt-4"
            imageBoxClassName="w-60 lg:w-[420px] flex items-center justify-center"
            imageClassName="w-full h-auto max-h-64 lg:max-h-[420px] object-contain"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            nextLabel="Continue"
            theme="dark"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
            primaryNote="It's hard to identify fraud online. Be cautious."
          />
        );

      // Case 25: Get reliable support DisplaySlide
      case 25:
        return (
          <DisplaySlide
            title="Get reliable support"
            description="We are available 24/7 for you. From transportation issues to communications with the medical staff, our English-fluent staff will help no matter the issue."
            imageSrc={getStellarSupport}
            imageAlt="Get reliable support illustration"
            imageSize="small"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            nextLabel="Continue"
            theme="dark"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
          />
        );

      // Case 26: TeamDisplaySlide
      case 26:
        return (
          <TeamDisplaySlide
            title="Here is your dedicated team."
            subtitle='Feel free to contact them 24/7.'
            description=""
            imageSrc={getStellarSupport}
            imageAlt="Team display illustration"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            nextLabel="Continue"
            theme="dark"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
            onAutoSave={handleAutoSave}
            sessionId={onboardingState.session?.id}
          />
        );

      // Case 27: ConversionSlide with dynamic name
      case 27:
        {
          const fullNameRaw = (latestOnboardingValues.fullLegalName || '').toString();
          const displayName = formatDisplayName(fullNameRaw, 20);
          // Build personalized title with first name when valid
          const firstWord = fullNameRaw ? fullNameRaw.trim().split(/\s+/)[0] : '';
          const isValidFirst = firstWord && /^[A-Za-zÀ-ÖØ-öø-ÿ'\-]{1,32}$/.test(firstWord);
          const firstWordCased = isValidFirst
            ? `${firstWord.charAt(0).toUpperCase()}${firstWord.slice(1).toLowerCase()}`
            : '';
          const conversionTitle = isValidFirst
            ? `${firstWordCased}, you are ready to get your hair back.`
            : `You are ready to get your hair back.`;
          const cardId = '00000000';
          const validUntil = ' - ';
          return (
          <ConversionSlide
            title={conversionTitle}
            description=""
            imageSrc={getStellarSupport}
            imageAlt="Conversion illustration"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            theme="dark"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
            showFooterSubtext={false}
            finalCardName={displayName}
            finalCardId={cardId}
            finalCardValidUntil={validUntil}
            // Session ID for loading before/after images from artifacts
            sessionId={onboardingState.session?.id}
            // Dynamic values for colored cards
            candidateScorePercent={candidateScorePercent}
            expectedCostValue={expectedCost.value}
            expectedCostLabel={expectedCost.label}
            expectedSatisfactionPercent={tikTokSatisfiedPercent}
            medicalHairLossPercent={medicalHairLossPercent}
            medicalStressPercent={medicalStressPercent}
            medicalSocialImpactPercent={medicalSocialImpactPercent}
            aestheticGoalsPercent={aestheticGoalsPercent}
          />
          );
        }

      // Case 28: ContactInfoSlide
      case 28:
        // Determine initial country code from passport selection (step 13)
        const getInitialCountryCode = () => {
          // First priority: use already saved phone region if exists
          if (latestOnboardingValues.primaryContactPhoneRegion) {
            return latestOnboardingValues.primaryContactPhoneRegion;
          }
          
          // Second priority: map from passport country selection
          const passportCountry = latestOnboardingValues.countryOfResidence;
          if (passportCountry) {
            const mappedCode = mapToPhoneCountryCode(passportCountry);
            if (mappedCode) {
              return mappedCode;
            }
          }
          
          // Fallback: United States
          return 'US';
        };

        return (
          <ContactInfoSlide
            title="How do we contact you?"
            subtitle='We need your phone number to facilitate the process with the appropriate clinics.'
            description=""
            imageSrc={getStellarSupport}
            imageAlt="Contact info illustration"
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            sessionId={onboardingState.session?.id}
            initialPhone={contactLocalNumber}
            initialCountryDial={contactDialCode || '+1'}
            initialCountryCode={getInitialCountryCode()}
            fieldName="primaryContactPhoneNumber"
            dialCodeFieldName="primaryContactDialCode"
            countryFieldName="primaryContactPhoneRegion"
            preferredChannelFieldName="preferredFollowUpChannel"
            nextLabel="Continue"
            theme="dark"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
            showFooterSubtext={false}
            onGoToEmail={() => {
              setAnimationDirection('forward');
              setIsTransitioning(true);
              setTimeout(() => setCurrentStep(28.5), 10);
              setTimeout(() => setIsTransitioning(false), 320);
            }}
          />
        );

      // Case 28.5: EmailSlide (optional branch from ContactInfo)
      case 28.5:
        return (
          <EmailSlide
            title="Please share your email"
            subtitle="We need your contact information to facilitate the process with the appropriate clinics."
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            onNext={goNext}
            onBack={goBack}
            onAutoSave={handleAutoSave}
            sessionId={onboardingState.session?.id}
            userName={latestOnboardingValues.fullLegalName || ''}
            theme="dark"
            animationDirection={animationDirection}
            isTransitioning={isTransitioning}
            // Allow switching back to phone entry
            onGoToPhone={() => {
              setAnimationDirection('backward');
              setIsTransitioning(true);
              setTimeout(() => setCurrentStep(28), 10);
              setTimeout(() => setIsTransitioning(false), 320);
            }}
          />
        );

      // Case 29: FinalConversionSlide
      case 29:
        {
          const rawName = (latestOnboardingValues.fullLegalName || '').toString().trim();
          const firstWord = rawName ? rawName.split(/\s+/)[0] : '';
          const isValidFirst = firstWord && /^[A-Za-zÀ-ÖØ-öø-ÿ'\-]{1,32}$/.test(firstWord);
          const firstWordCased = isValidFirst
            ? `${firstWord.charAt(0).toUpperCase()}${firstWord.slice(1).toLowerCase()}`
            : '';
          const finalTitle = isValidFirst
            ? `Congratulations ${firstWordCased}, we'll be in touch shortly.`
            : `Congratulations, we'll be in touch shortly.`;

          const fullNameRaw = (latestOnboardingValues.fullLegalName || '').toString();
          const finalCardName = formatDisplayName(fullNameRaw, 20);
          const finalCardId = computeIdSuffix(onboardingState.session?.id);
          const finalCardValidUntil = computeValidUntilPlusMonths(4);

          return (
            <FinalConversionSlide
              title={finalTitle}
              currentStep={currentStep}
              totalSteps={TOTAL_STEPS}
              onNext={goNext}
              onBack={goBack}
              theme="dark"
              animationDirection={animationDirection}
              isTransitioning={isTransitioning}
              finalCardName={finalCardName}
              finalCardId={finalCardId}
              finalCardValidUntil={finalCardValidUntil}
              // preliminary colored card values
              candidateScorePercent={candidateScorePercent}
              expectedCostValue={expectedCost.value}
              expectedCostLabel={expectedCost.label}
              expectedSatisfactionPercent={tikTokSatisfiedPercent}
              medicalHairLossPercent={medicalHairLossPercent}
              medicalStressPercent={medicalStressPercent}
              medicalSocialImpactPercent={medicalSocialImpactPercent}
              aestheticGoalsPercent={aestheticGoalsPercent}
            />
          );
        }

      default:
        // Invalid step - redirect to start
        console.error('[OnboardingPage] Invalid step reached:', currentStep);
        // Use setTimeout to avoid state update during render
        setTimeout(() => {
          setCurrentStep(0);
        }, 0);
        return null;
    }
  };

  return (
    <div className="min-h-screen">
      <HelmetTemplate
        title="Match Medic"
        description="Welcome to Match Medic – Find the right hair transplant clinic for your unique needs. Cut through the noise and misinformation surrounding hair transplants. Our algorithm will recommend the clinics with the best track record for your unique needs within seconds."
        canonicalUrl="https://matchmedic.com/onboarding"
      />
      <React.Fragment key={`onboarding-step-${currentStep}`}>
        {renderSlide()}
      </React.Fragment>
    </div>
  );
}
